<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
				<div class="col-md-12">
          <h3 v-if="template.name"><i class="fa fa-fw fa-file-text"></i> {{ template.name }}</h3>
          <h3 v-else><i class="fa fa-fw fa-file-text"></i> {{ _('New template') }}</h3>
          <hr style="margin-bottom: 0" />
        </div>
      </div>
      <lock-info v-if="locked" :locked="locked" :lockStatus="lockStatus" v-on:forceLock="getComponentLock(true)" />
      <div class="row">
        <div class="col-sm-6 leftcol">
          <h3>{{ _('General') }}</h3>
          <div class="form-group" style="height: 65px">
            <label for="name">{{ _('Name') }}</label>
            <input type="text" class="form-control" id="templateName" v-model="template.name" :placeholder="_('Enter template name')" />
          </div>
          <div class="form-group">
            <label for="type">{{ _('Type') }}</label>
            <select class="form-control" v-model="template.type">
              <template v-if="!isPdfValuecard">
                <option value="posreceipt.tpl">{{ _('POS receipt') }}</option>
                <option value="posticket.tpl">{{ _('POS ticket') }}</option>
                <option value="bocaticket.tpl">{{ _('POS ticket') }} (BOCA)</option>
              </template>
              <option v-else value="pdfvaluecard.tpl">{{ _('PDF value card') }}</option>
            </select>
          </div>
          <div v-if="!isPdfValuecard" class="form-group">
            <label for="codes">{{ _('Data') }}</label>
            <textarea id="templateData" class="form-control" wrap="virtual" rows="20" v-model="template.data"></textarea>
          </div>
          <div v-else class="form-group">
            <label for="margins">{{ _('Margins') }}</label>
            <div class="input-group">
              <span class="input-group-addon" style="width: 80px; text-align: right">{{ _('Top') }}</span>
              <input type="number" data-android-inputmode="decimal" name="margintop" class="form-control" placeholder="0" v-model="marginTop" />
              <span class="input-group-addon">{{ _('mm') }}</span>
            </div>
          </div>
          <div class="form-group">
            <div class="input-group">
              <span class="input-group-addon" style="width: 80px; text-align: right">{{ _('Bottom') }}</span>
              <input type="number" data-android-inputmode="decimal" name="marginbottom" class="form-control" placeholder="0" v-model="marginBottom" />
              <span class="input-group-addon">{{ _('mm') }}</span>
            </div>
          </div>
          <div class="form-group">
            <div class="input-group">
              <span class="input-group-addon" style="width: 80px; text-align: right">{{ _('Left') }}</span>
              <input type="number" data-android-inputmode="decimal" name="marginleft" class="form-control" placeholder="0" v-model="marginLeft" />
              <span class="input-group-addon">{{ _('mm') }}</span>
            </div>
          </div>
          <div class="form-group">
            <div class="input-group">
              <span class="input-group-addon" style="width: 80px; text-align: right">{{ _('Right') }}</span>
              <input type="number" data-android-inputmode="decimal" name="marginright" class="form-control" placeholder="0" v-model="marginRight" />
              <span class="input-group-addon">{{ _('mm') }}</span>
            </div>
          </div>
          <div class="form-group">
            <label for="background">{{ _('Background') }}</label>
            <image-slot :file-id="file ? file.id : null" :ext="file && file.ext ? file.ext : ''" :owner-key="uploadOwnerKey" upload-slot="background" :show-details="false" input-id="backgroundImageUpload" image-element-id="templateImage" v-on:upload="queueUploads($event, 'background')" v-on:library="chooseFromLibrary('background')" v-on:remove="file = null" />
          </div>
        </div>
        <div v-if="!isPdfValuecard" class="col-sm-6 rightcol">
          <h3>&nbsp;</h3>
          <div class="form-group">
            <label for="props">{{ _('Points of use') }}</label><br />
            <template v-for="pos in poses">
              <label style="font-weight: normal; margin-bottom: 0px">
                <input type="checkbox" v-model="props" :value="pos.id" /> {{ pos.name }}
              </label>
              <br />
            </template>
          </div>
        </div>
        <div v-else class="col-sm-6 rightcol">
          <h3>&nbsp;</h3>
          <div class="form-group" style="min-height: 65px">
            <label for="products">{{ _('Products') }}</label><br />
            <multiselect v-model="products" :options="allProducts" :multiple="true" :searchable="true" :allow-empty="true" :custom-label="nameWithId" label="name" track-by="id">
              <template #noResult>{{ _('No results found.') }}</template>
            </multiselect>
          </div>
          <div class="form-group">
            <label for="size">{{ _('Size') }}</label>
            <select class="form-control" v-model="size">
              <option value="A4">A4</option>
              <option value="A4L">A4 ({{ _('landscape') }})</option>
              <option value="A5">A5</option>
              <option value="A5L">A5 ({{ _('landscape') }})</option>
              <option value="A6">A6</option>
              <option value="A6L">A6 ({{ _('landscape') }})</option>
            </select>
          </div>
          <div class="form-group">
            <label for="font">{{ _('Font') }}</label>
            <select class="form-control" v-model="font">
              <option value="Anton">Anton</option>
              <option value="BalooBhai">Baloo Bhai</option>
              <option value="Caveat">Caveat</option>
              <option value="JosefinSans">Josefin Sans</option>
              <option value="McLaren">McLaren</option>
              <option value="Merriweather">Merriweather</option>
              <option value="NotoSans">Noto Sans</option>
              <option value="Pacifico">Pacifico</option>
              <option value="Play">Play</option>
              <option value="PlayfairDisplay">Playfair Display</option>
            </select>
          </div>
          <div class="form-group">
            <label for="fontsize">{{ _('Font size') }} (pt)</label>
            <select class="form-control" v-model="fontSize">
              <option v-for="size in Array.from({length: 14}, (_, i) => i + 7)" :value="size">{{ size }}</option>
            </select>
          </div>
          <div class="form-group">
            <label for="textalign">{{ _('Text alignment') }}</label>
            <select class="form-control" v-model="textAlign">
              <option value="left">{{ _('Left') }}</option>
              <option value="center">{{ _('Center') }}</option>
              <option alue="right">{{ _('Right') }}</option>
            </select>
          </div>
          <div class="form-group">
            <label for="message">{{ _('Message') }}</label>
            <textarea id="templateMessage" class="form-control" wrap="virtual" rows="18" v-model="message"></textarea>
          </div>
          <div class="form-group">
            <label for="terms">{{ _('Terms') }}</label>
            <textarea id="templateTerms" class="form-control" wrap="virtual" rows="6" v-model="terms"></textarea>
          </div>
        </div>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <button class="btn btn-danger" v-on:click="remove" type="button" :disabled="!isRemoveButtonEnabled"><i class="fa fa-fw fa-trash"></i> {{ _('Delete') }}</button>
        <div class="button-col">
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>

import axios from 'axios'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import uploadaware from '~/lib/uploadaware'
import Multiselect from 'vue-multiselect'

export default {
  data () {
    return {
      allProducts: [],
      template: {},
      poses: []
    }
  },
  mixins: [changewatcher, components, uploadaware],
  computed: {
    isPdfValuecard () {
      return this.template && this.template.type === 'pdfvaluecard.tpl'
    },
    marginTop: {
      get () {
        return this.template && this.template.data ? this.template.data.margintop : 0
      },
      set (newValue) {
        this.template.data.margintop = newValue
      }
    },
    marginBottom: {
      get () {
        return this.template && this.template.data ? this.template.data.marginbottom : 0
      },
      set (newValue) {
        this.template.data.marginbottom = newValue
      }
    },
    marginLeft: {
      get () {
        return this.template && this.template.data ? this.template.data.marginleft : 0
      },
      set (newValue) {
        this.template.data.marginleft = newValue
      }
    },
    marginRight: {
      get () {
        return this.template && this.template.data ? this.template.data.marginright : 0
      },
      set (newValue) {
        this.template.data.marginright = newValue
      }
    },
    file: {
      get () {
        return this.template && this.template.data && this.template.data.file_id ? { id: this.template.data.file_id, ext: this.template.data.file_ext } : null
      },
      set (newValue) {
        this.template.data.file_id = newValue ? newValue.id : null
        this.template.data.file_ext = newValue ? newValue.ext : null
      }
    },
    uploadOwner () {
      if (!this.template || !this.template.data) {
        return null
      }
      const id = this.template.id
      const label = this._('Template') + (id ? ' ' + id : '') + (this.template.name ? ': ' + this.template.name : '')
      return {
        type: 'template',
        id,
        label,
        open: {
          component: 'TemplateDetails',
          title: label,
          width: 6,
          data: {
            templateId: id
          }
        }
      }
    },
    size: {
      get () {
        return this.template && this.template.data ? this.template.data.size : null
      },
      set (newValue) {
        this.template.data.size = newValue
      }
    },
    font: {
      get () {
        return this.template && this.template.data ? this.template.data.font : null
      },
      set (newValue) {
        this.template.data.font = newValue
      }
    },
    fontSize: {
      get () {
        return this.template && this.template.data ? this.template.data.fontsize : null
      },
      set (newValue) {
        this.template.data.fontsize = newValue
      }
    },
    textAlign: {
      get () {
        return this.template && this.template.data ? this.template.data.textalign : null
      },
      set (newValue) {
        this.template.data.textalign = newValue
      }
    },
    message: {
      get () {
        return this.template && this.template.data ? this.template.data.message : ''
      },
      set (newValue) {
        this.template.data.message = newValue
      }
    },
    terms: {
      get () {
        return this.template && this.template.data ? this.template.data.terms : ''
      },
      set (newValue) {
        this.template.data.terms = newValue
      }
    },
    props: {
      get () {
        return this.template && this.template.props ? JSON.parse(this.template.props) : []
      },
      set (newValue) {
        this.template.props = JSON.stringify(newValue)
      }
    },
    products: {
      get () {
        return this.props.map(p => {
          const [type, id] = p.split('-')
          const prod = this.allProducts.find(prod => prod.id == id)
          let name = ''
          if (prod) {
            name = prod.name
          }
          return { id: id, name: name }
        })
      },
      set (newValue) {
        this.props = newValue.map(v => 'product-' + v.id)
      }
    }
  },
  methods: {
    async loadPoses () {
      let response
      try {
        response = await axios.get(this.baseDomain + this.shopId + '/' + this.locale + '/pos.json?version=' + this.locale + '&iSortCol_0=1&sSortDir_0=asc&iSortingCols=1&apikey=' + this.apiKey)
      } catch(e) {
        this.showError(e)
        return
      }
      this.poses = response.data.map(item => ({...item, id: `pos-${item.id}`})).sort((a, b) => a.name > b.name ? 1 : -1)
    },
    // Puts a file uploaded through the upload queue in its place (lib/uploadaware.js)
    applyUploadResult (slot, file) {
      if (slot === 'background' && file && this.template && this.template.data) {
        this.file = { id: file.id, ext: file.ext }
      }
    },
    nameWithId ({ id, name }) {
      return id + ': ' + name
    },
    getSaveData () {
      const exportFields = [
        'id',
        'name',
        'type',
      ]
      const data = exportFields.reduce((a, e) => (a[e] = this.template[e], a), {})
      if (this.isPdfValuecard) {
        if (this.template.data) {
          Object.keys(this.template.data).forEach(key => {
            data[key] = this.template.data[key]
          })
        }
        data.products = this.products.map(p => p.id)
      } else {
        data.data = this.template.data
        data.props = this.props
      }
      return data
    },
    async doRemove () {
      let response
      try {
        this.deleted = true
        response = await axios.delete(this.baseDomain + this.shopId + '/templates/' + this.template.id + '.json?apikey=' + this.apiKey)
      } catch(e) {
        this.deleted = false
        return this.showError(this._('Deleting the template failed!'))
      }
      this.showNotification(this._('Template deleted!'))
      this.close()
    },
    async save () {
      await this.awaitUploadsBeforeSave()
      this.saving = true
      const data = this.getSaveData()
      let response
      try {
        if (data.id) {
          response = await axios.put(this.baseDomain + this.shopId + '/templates/' + data.id + '.json?apikey=' + this.apiKey, data)
        } else {
          delete data.id
          response = await axios.post(this.baseDomain + this.shopId + '/templates.json?apikey=' + this.apiKey, data)
        }
      } catch(e) {
        this.saving = false
        this.applyPendingUploads()
        return this.showError(e)
      }
      if (response && response.data && response.data.id) {
        await this.loadTemplate(response.data.id)
      }
      this.saving = false
      this.saved = true
      this.storeState()
      this.applyPendingUploads()
      setTimeout(() => { this.saved = false }, 1000)
      this.showNotification(this._('Changes saved successfully!'))
      this.$emit('save', this.resource)
      if (this.saveShouldClose) {
        this.close()
      }
    },
    loadTemplate (id) {
      return new Promise(async (resolve, reject) => {
        this.loading = true
        let response
        try {
          response = await axios.get(this.baseDomain + this.shopId + '/templates/' + id + '.json?apikey=' + this.apiKey)
        } catch (e) {
          this.loading = false
          this.showError(e)
          reject()
          return
        }
        this.template = response.data
        if (this.isPdfValuecard) {
          this.template.data = JSON.parse(this.template.data)
        }
        if (!this.template.data) {
          this.template.data = {
            margintop: 0,
            marginleft: 0,
            marginright: 0,
            marginbottom: 0,
            file_id: null,
            file_ext: null,
            size: null,
            font: null,
            fontsize: null,
            textalign: null, 
            message: '',
            terms: '',
            props: [],
          }
        }
        this.storeState()
        this.loading = false
        if (!this.saving) { // save() applies them after storing the saved state
          this.applyPendingUploads()
        }
        resolve(this.template)
      })
    },
    async loadProducts () {
      let response = await axios.get(this.baseDomain + this.shopId + '/' + this.locale + '/products.json?version=' + this.locale + '&sSearch_3=0-anyvaluecard&iSortCol_0=2&sSortDir_0=asc&iSortingCols=1&simple=true')
      let ret = []
      if (response && response.data) {
        for (var i = 0; i < response.data.length; i++) {
          let d = response.data[i]
          ret.push({ id: d.id, name: d.name })
        }
      }
      this.allProducts = ret
    },
  },
  components: {
    Multiselect
  },
  created () {
    if (this.component && this.component.initData) {
      this.loadTemplate(this.component.initData.templateId)
    }
    this.loadProducts()
    this.loadPoses()
  },
  async updated () {
    if (this.component && this.component.initData) {
      if (this.template && this.template.id && parseInt(this.template.id, 10) !== parseInt(this.component.initData.templateId, 10)) {
        try {
          await this.loadTemplate(this.component.initData.templateId)
        } catch(e) {}
      }
    }
  },
  async mounted () {
    if(!this.events) {
      let events = {}
      events['templateUpdate'] = async (message) => {
        if (message && message.data && this.template && parseInt(message.data.id, 10) === parseInt(this.template.id, 10)) {
          if (!this.unsavedChanges) { // Only update if we haven't made any changes
            this.loadTemplate(message.data.id)
          }
        }
      }
      this.events = events
    }
    this.enableEventBus()
  },
}
</script>

<style scoped>
#templateData {
  font-family: ConsolasMonaco,Lucida Console,Liberation Mono,DejaVu Sans Mono,Bitstream Vera Sans Mono,Courier New, monospace;
}
</style>
